import React, { useCallback, useState } from 'react';

import cn from 'classnames';
import { get, isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { Button } from '@shared/components';
import TextArea from '@shared/components/Input/TextArea';
import { intl } from '@shared/localization';

import styles from './AdvRequirementsComments.module.scss';
import CommentImage from './CommentImage';
import type { TransformedCommentPhoto } from './use-advancement-comments';

type RequirementCommentProps = {
  author?: string | null;
  authorImage: string;
  commentId: number;
  date?: string | null;
  disabled?: boolean;
  message: string;
  onDeleteComment: (userId: number, commentId: number) => Promise<void>;
  onDeleteCommentPhoto: (
    userId: number,
    commentId: number,
    photoId: number,
  ) => Promise<void>;
  onEditComment: (
    userId: number,
    commentId: number,
    comment: string,
  ) => Promise<void>;
  photos?: TransformedCommentPhoto[];
  renderPhotos?: boolean;
  userId: number;
  versionId?: number | null;
  viewerMemberId?: number | null;
};

const RequirementComment = (props: RequirementCommentProps) => {
  const {
    author = null,
    authorImage,
    commentId,
    date = null,
    disabled = false,
    message,
    onDeleteComment,
    onDeleteCommentPhoto,
    onEditComment,
    photos = [],
    renderPhotos = false,
    userId,
    viewerMemberId = null,
  } = props;

  const [editCommentMessage, setEditCommentMessage] = useState(message);
  const [edit, toggleEdit] = useState(false);

  const handleEditComment = useCallback(() => {
    onEditComment(userId, commentId, editCommentMessage);

    toggleEdit(false);
  }, [commentId, editCommentMessage, onEditComment, userId]);

  const handleDeleteComment = useCallback(() => {
    onDeleteComment(userId, commentId);
  }, [commentId, onDeleteComment, userId]);

  const handleDeletePhoto = useCallback(() => {
    if (!isEmpty(photos)) {
      onDeleteCommentPhoto(userId, commentId, get(photos, [0, 'id']));
    }
  }, [commentId, onDeleteCommentPhoto, photos, userId]);
  const onToggleEdit = useCallback(() => toggleEdit(!edit), [edit]);
  const setDefaultAvatar = (event: React.SyntheticEvent<HTMLImageElement>) =>
    (event.currentTarget.src = '');
  const canEdit = !disabled && viewerMemberId === userId;
  const saveDisabled =
    editCommentMessage === message || isEmpty(editCommentMessage);

  return (
    <div className={styles.requirementComment}>
      <div className={styles.requirementCommentAvatarContainer}>
        {!isEmpty(authorImage) && (
          <img
            className={styles.requirementCommentAvatar}
            loading="lazy"
            onError={setDefaultAvatar}
            src={authorImage}
          />
        )}
      </div>
      <div className={styles.requirementCommentContent}>
        <div className={styles.requirementCommentHeader}>
          <span className={cn('title-5', styles.requirementCommentAuthor)}>
            {author}
          </span>
          <span className="subtitle-6">{date}</span>
        </div>
        {!edit && (
          <div className={styles.requirementCommentMessage}>{message}</div>
        )}
        {edit && (
          <div className={styles.requirementCommentMessage}>
            <div className={styles.requirementCommentInput}>
              <TextArea
                autoSize={{ minRows: 2 }}
                charCounter
                charCounterBelow
                disabled={disabled}
                label={intl.formatMessage({
                  id: 'AdvRequirementsComments.comment',
                })}
                maxLength={200}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setEditCommentMessage(event.target.value)
                }
                placeholder={intl.formatMessage({
                  id: 'AdvRequirementsComments.addComment',
                })}
                value={editCommentMessage}
              />
            </div>
          </div>
        )}
        <div className={styles.requirementCommentActions}>
          <div className={styles.requirementCommentActionsLeft}>
            {renderPhotos &&
              photos.map(photo => (
                <CommentImage
                  editable={edit}
                  key={photo.id}
                  onDeletePhoto={handleDeletePhoto}
                  photo={photo}
                />
              ))}
          </div>
          {edit && (
            <>
              <Button
                className={styles.requirementCommentActionButton}
                fitText
                ghost
                noBorder
                onClick={onToggleEdit}
                size="small"
                uppercase={false}
              >
                <FormattedMessage id="AdvRequirementsComments.cancel" />
              </Button>
              <Button
                className={styles.requirementCommentActionButton}
                disabled={saveDisabled}
                fitText
                ghost
                noBorder
                onClick={handleEditComment}
                uppercase={false}
              >
                <FormattedMessage id="AdvRequirementsComments.save" />
              </Button>
            </>
          )}
          {canEdit && !edit && (
            <>
              <Button
                className={styles.requirementCommentActionButton}
                fitText
                ghost
                noBorder
                onClick={onToggleEdit}
                uppercase={false}
              >
                <FormattedMessage id="AdvRequirementsComments.edit" />
              </Button>
              <Button
                className={styles.requirementCommentActionButton}
                fitText
                ghost
                noBorder
                onClick={handleDeleteComment}
                uppercase={false}
              >
                <FormattedMessage id="AdvRequirementsComments.delete" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequirementComment;
